import { useTranslation } from 'react-i18next'

import { bandLabel, bandOf } from '../../lib/scoreBand'
import { faDigits } from '../../lib/numerals'
import { xpSplit } from '../../lib/review'
import Badge from '../common/Badge'

const RING_RADIUS = 52
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS

/* good/warn/bad are theme tokens (index.css) — var(...) so the ring repaints
   under dark mode the same way every Tailwind utility referencing them does. */
const RING_STROKE = {
  good: 'var(--color-good)',
  warn: 'var(--color-warn)',
  bad: 'var(--color-bad)',
}

function round(value) {
  return value === null || value === undefined ? null : Math.round(Number(value))
}

/**
 * The score ring — Result's signature element in both the design canvas and
 * the Android app. Colour is never the only signal for the band (the label
 * underneath repeats it in words), but the ring is still the fastest read:
 * how far around it goes *is* the score.
 */
function ScoreRing({ score }) {
  const { t } = useTranslation('review')
  const band = score === null ? null : bandOf(score)
  const progress = score === null ? 0 : score / 100
  const offset = RING_CIRCUMFERENCE * (1 - progress)

  return (
    <div className="relative inline-grid size-32 place-items-center">
      <svg viewBox="0 0 120 120" className="size-32 -rotate-90">
        <circle cx="60" cy="60" r={RING_RADIUS} fill="none" strokeWidth="10" className="stroke-slate-100 dark:stroke-slate-700" />
        {band && (
          <circle
            cx="60"
            cy="60"
            r={RING_RADIUS}
            fill="none"
            strokeWidth="10"
            strokeLinecap="round"
            stroke={RING_STROKE[band]}
            strokeDasharray={RING_CIRCUMFERENCE}
            strokeDashoffset={offset}
            className="transition-[stroke-dashoffset] duration-700"
          />
        )}
      </svg>
      <div className="absolute flex flex-col items-center">
        <span className="text-3xl font-bold text-slate-800 dark:text-slate-100">
          {score === null ? '—' : faDigits(score)}
        </span>
        <span className="text-xs text-slate-400 dark:text-slate-500">{t('score.outOf100')}</span>
      </div>
    </div>
  )
}

export default function ScoreSummary({ assessment, xpAwarded, aiXp, socialXp }) {
  const { t } = useTranslation('review')
  const overall = round(assessment?.overall)
  const band = overall === null ? null : bandOf(overall)
  const split = xpSplit(aiXp, socialXp)

  /** The four Azure sub-scores, plus the headline number — translated fresh on every render. */
  const METRICS = [
    { key: 'accuracy', label: t('score.accuracy') },
    { key: 'fluency', label: t('score.fluency') },
    { key: 'completeness', label: t('score.completeness') },
    { key: 'prosody', label: t('score.prosody') },
  ]

  return (
    <div>
      <div className="flex items-center gap-5">
        <ScoreRing score={overall} />
        <div>
          {band && (
            <p className="font-semibold" style={{ color: RING_STROKE[band] }}>
              {bandLabel(band)}
            </p>
          )}
          {/* Proposal §5.4 (compliance row practice-15): the analysis is shown as
              an automated output. Owner, 2026-10-16: this short label is how —
              it replaced the disclaimer sentence (no explanatory text). */}
          <p data-automated-analysis="true" className="text-[11px] text-slate-400 dark:text-slate-500">
            {t('score.automated')}
          </p>
          {split ? (
            <div className="mt-1.5 space-y-1" data-xp-split="true">
              <Badge tone="brand">{t('score.xpAwarded', { xp: faDigits(split.total) })}</Badge>
              <p className="tnum text-xs text-slate-500 dark:text-slate-400">
                {t('score.xpSplit', { ai: faDigits(split.ai), social: faDigits(split.social) })}
              </p>
            </div>
          ) : (
            xpAwarded > 0 && (
              <Badge tone="brand" className="mt-1.5">
                {t('score.xpAwarded', { xp: faDigits(xpAwarded) })}
              </Badge>
            )
          )}
        </div>
      </div>

      <dl className="mt-6 grid grid-cols-2 gap-4">
        {METRICS.map(({ key, label }) => {
          const value = round(assessment?.[key])
          return (
            <div key={key}>
              <dt className="text-xs text-slate-500 dark:text-slate-400">{label}</dt>
              <dd className="mt-1 text-lg font-semibold text-slate-800 dark:text-slate-100">
                {/* Prosody is only returned for long enough audio, so a
                    missing value is normal rather than an error. */}
                {value === null ? '—' : faDigits(value)}
              </dd>
              <div
                role="progressbar"
                aria-valuenow={value ?? 0}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-label={label}
                className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-700"
              >
                <div
                  className="h-full rounded-full bg-brand-500 transition-[width] duration-700"
                  style={{ width: `${value ?? 0}%` }}
                />
              </div>
            </div>
          )
        })}
      </dl>
    </div>
  )
}
